// VariantPicker — dropdowns for picking a Shopify product's variant
// (Material/Color/Quantity/etc.) inside a Studio card.
//
// Designed to be the single qty/variant-selection surface across the Studio:
//   * Quick Reorder card (Material/Color swap + Quantity picker + price label)
//   * (Future) Detail Panel "reorder with options"
//   * (Future) Home Continue card "reorder with options"
//
// Behavior:
//   - Fetches the product's full option metadata for the design's current
//     variant via /storefront/studio/designs/:id/variant-options.
//   - Renders one <select> per option group. The Quantity option's labels
//     embed price ("4 — $38.00"); other options just show their value.
//   - Single-value option groups still render but are disabled (a custom
//     swatch or hint could replace them later, but for v1 we keep it simple).
//   - Out-of-stock variant combos disable just that option (e.g. PVC qty 25
//     might be sold out — disable that single option entry).
//   - The "Last time" badge marks the qty option matching the design's
//     last_ordered_qty so customers can re-pick it in one tap.
//   - Whenever the resolved variant changes, calls onChange with
//     {variant_id, qty, price, available} or null when no resolution exists.
//
// When `has_picker` is false (single-SKU products), this component renders
// nothing. Parent should fall back to a default reorder action.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDesignVariantOptions } from '../lib/api';
import { qk } from '../lib/queryClient';
import { fmtDollars, fmtQty } from '../lib/format';
import type { VariantOptionsResponse, VariantOptionRow } from '../lib/types';

export interface VariantPickerSelection {
  variant_id: number;
  /** When the product has a `Quantity` option group, this is the value
   *  baked into the picked variant (e.g. 9 for "ALUMINUM COMPOSITE / 9").
   *  When the product has NO `Quantity` option, this is always 1 — the
   *  parent component is responsible for rendering its own cart-qty
   *  stepper to multiply the variant price. See qtyFromPicker. */
  qty: number;
  /** Variant's published price.
   *  - When qtyFromPicker=true: the full tier price (e.g. $67.50 for qty 9).
   *  - When qtyFromPicker=false: the per-unit price (e.g. $9.00 for one). */
  price: number;
  available: boolean;
  /** Map of option-name -> chosen value, mirrored back for the parent. */
  options: Record<string, string>;
  /** True iff the product has a `Quantity` option group (Model A — bulk
   *  pricing baked into variants). False for Model B/C products where the
   *  parent should render a separate cart-qty stepper. */
  qtyFromPicker: boolean;
}

export interface VariantPickerProps {
  designId: string;
  /** Hint to mark the matching qty option as "last time". */
  lastOrderedQty?: number | null;
  /** Fires whenever the resolved variant changes. Null when no exact match
   *  exists for the current selection (rare; typically out-of-stock combo). */
  onChange: (selection: VariantPickerSelection | null) => void;
  /** Fires once when the initial fetch completes, with the boolean indicating
   *  whether this product has any options. Lets the parent show a fallback
   *  reorder button when there's no picker. */
  onReady?: (hasPicker: boolean) => void;
}

/** Find the variant whose options exactly match the given selection. */
function findMatchingVariant(
  variants: VariantOptionRow[],
  selection: Record<string, string>,
): VariantOptionRow | null {
  for (const v of variants) {
    let match = true;
    for (const [k, val] of Object.entries(selection)) {
      if (v.options[k] !== val) {
        match = false;
        break;
      }
    }
    if (match) return v;
  }
  return null;
}

/** Given the current selection (excluding `optionName`), find which values
 *  for `optionName` are still available (i.e., would resolve to a real,
 *  in-stock variant when chosen). Used to grey-out impossible options. */
function availableValuesFor(
  variants: VariantOptionRow[],
  optionName: string,
  otherSelection: Record<string, string>,
): { value: string; available: boolean }[] {
  const seen = new Map<string, boolean>();
  for (const v of variants) {
    let match = true;
    for (const [k, val] of Object.entries(otherSelection)) {
      if (k === optionName) continue;
      if (v.options[k] !== val) {
        match = false;
        break;
      }
    }
    if (!match) continue;
    const value = v.options[optionName];
    if (value == null) continue;
    // Mark available if any matching variant is in-stock; otherwise false.
    seen.set(value, (seen.get(value) ?? false) || v.available_for_sale);
  }
  return Array.from(seen.entries()).map(([value, available]) => ({ value, available }));
}

export function VariantPicker({ designId, lastOrderedQty, onChange, onReady }: VariantPickerProps) {
  const query = useQuery<VariantOptionsResponse>({
    queryKey: qk.designVariantOptions(designId),
    queryFn: () => getDesignVariantOptions(designId),
    staleTime: 5 * 60_000,
  });
  const [selection, setSelection] = useState<Record<string, string> | null>(null);

  // Seed the selection once the data lands, defaulting to the design's
  // currently-known variant. If lastOrderedQty hints a specific qty value
  // that exists in the variant pool, prefer that for the Quantity option.
  useEffect(() => {
    if (!query.data || selection !== null) return;
    if (!query.data.has_picker) {
      onReady?.(false);
      onChange(null);
      return;
    }
    const seed: Record<string, string> = { ...(query.data.current?.options ?? {}) };
    if (lastOrderedQty != null) {
      const qtyOpt = query.data.options.find((o) => o.is_quantity);
      if (qtyOpt) {
        const desired = String(lastOrderedQty);
        if (qtyOpt.values.includes(desired)) {
          seed[qtyOpt.name] = desired;
        }
      }
    }
    setSelection(seed);
    onReady?.(true);
  }, [query.data, lastOrderedQty, selection, onChange, onReady]);

  // Fire onChange whenever the resolved variant changes.
  useEffect(() => {
    if (!query.data || !selection) return;
    const matched = findMatchingVariant(query.data.variants, selection);
    if (!matched) {
      onChange(null);
      return;
    }
    const qtyOpt = query.data.options.find((o) => o.is_quantity);
    const qtyFromPicker = !!qtyOpt;
    const qty = qtyOpt ? parseInt(matched.options[qtyOpt.name] ?? '1', 10) || 1 : 1;
    onChange({
      variant_id: matched.variant_id,
      qty,
      price: matched.price,
      available: matched.available_for_sale,
      options: matched.options,
      qtyFromPicker,
    });
  }, [query.data, selection, onChange]);

  const optionsToRender = useMemo(() => {
    if (!query.data || !selection) return [];
    return query.data.options.map((opt) => {
      const valuesWithAvailability = availableValuesFor(query.data.variants, opt.name, selection);
      return {
        ...opt,
        valuesWithAvailability,
      };
    });
  }, [query.data, selection]);

  if (query.isLoading) {
    return <div className="variant-picker variant-picker--loading">Loading options…</div>;
  }
  if (!query.data || !query.data.has_picker || !selection) return null;

  const qtyOptionName = query.data.options.find((o) => o.is_quantity)?.name ?? null;
  const lastQtyStr = lastOrderedQty != null ? String(lastOrderedQty) : null;

  // Quick lookup of price by option-value combo for the QUANTITY dropdown
  // labels — we want to show "(qty) — $price" inline.
  const priceByQtyAndOthers = (qtyValue: string): { price: number; available: boolean } | null => {
    if (!qtyOptionName) return null;
    const probe = { ...selection, [qtyOptionName]: qtyValue };
    const v = findMatchingVariant(query.data.variants, probe);
    return v ? { price: v.price, available: v.available_for_sale } : null;
  };

  return (
    <div className="variant-picker" onClick={(e) => e.stopPropagation()}>
      {optionsToRender.map((opt) => {
        // Hide single-value option groups — they convey no choice and would
        // just be visual noise. The selection still includes them.
        if (opt.values.length <= 1) return null;
        const isQty = opt.is_quantity;
        return (
          <label key={opt.name} className="variant-picker__row">
            <span className="variant-picker__label">{opt.name}</span>
            <select
              className="chip chip--select variant-picker__select"
              value={selection[opt.name] ?? ''}
              onChange={(e) => setSelection((s) => ({ ...(s ?? {}), [opt.name]: e.target.value }))}
            >
              {opt.valuesWithAvailability.map(({ value, available }) => {
                let label = value;
                if (isQty) {
                  const tier = priceByQtyAndOthers(value);
                  if (tier) {
                    label = `${fmtQty(parseInt(value, 10) || 0)} — ${fmtDollars(tier.price)}`;
                  } else {
                    label = `${value}`;
                  }
                  if (lastQtyStr === value) label += ' · last time';
                }
                if (!available) label += ' (out of stock)';
                return (
                  <option key={value} value={value} disabled={!available}>
                    {label}
                  </option>
                );
              })}
            </select>
          </label>
        );
      })}
    </div>
  );
}
